import { TDropdownItem, TLiftedFormModel } from '../../model/form.model';
import { PIPELINE_FORM } from '../../model/form.pipeline';
import { FORM_MODIFIER } from '../../model/form.modifier';
import { TRESPONSE_GET_MACHINE_AND_LINE_DETAILS } from '@monorepo/types';

const NEEDLE_TYPES = [
  {
    displayName: 'tm_raise_request.Broken',
    value: 'BROKEN',
  },
  {
    displayName: 'tm_raise_request.ChangeOver',
    value: 'CHANGEOVER',
  },
];

export type TRaiseNeedleRequestRequestedNeedleFormPayload = { needleId: [number]; quantity: number };

export type TRaiseNeedleRequestCurrentNeedleFormPayload = TRaiseNeedleRequestRequestedNeedleFormPayload & {
  brokenQuantity?: number;
};

export const RAISE_NEEDLE_REQUEST_FORM_MODEL = {
  createReasonForm: (): TLiftedFormModel => {
    return PIPELINE_FORM.sanitize({
      inputs: [
        {
          input: {
            type: 'DROPDOWN',
            options: NEEDLE_TYPES,
          },
          metadata: {
            keyName: 'reason',
            label: 'tm_raise_request.RequestReason',
            isRequired: true,
          },
        },
      ],
      colConfig: 'SINGLE_COL',
    });
  },

  createCurrentNeedleModel: (
    assignedNeedles: TRESPONSE_GET_MACHINE_AND_LINE_DETAILS['assignedNeedles'],
  ): TLiftedFormModel => {
    return PIPELINE_FORM.sanitize({
      inputs: [
        {
          input: {
            type: 'DROPDOWN_WITH_SEARCH',
            options: assignedNeedles.map((needle) => ({
              value: needle.needleId,
              displayName: `${needle.needleNumber} / ${needle.needleBrand} / ${needle.needleType}`,
            })),
          },
          metadata: {
            keyName: 'needleId',
            isRequired: true,
            label: 'Needle Number / Needle Brand / Needle Type',
          },
        },
        {
          input: {
            type: 'INPUT',
            subType: 'number',
            min: 1,
          },
          metadata: {
            keyName: 'quantity',
            label: 'tm_raise_request.ReturnedQuantity',
            isRequired: true,
            dependsOn: {
              keyName: 'needleNumber',
            },
          },
        },
        {
          input: {
            type: 'INPUT',
            subType: 'number',
            min: 1,
          },
          metadata: {
            keyName: 'brokenQuantity',
            label: 'tm_raise_request.BrokenQuantity',
            isRequired: false,
            dependsOn: {
              keyName: 'needleNumber',
            },
          },
        },
      ],
      colConfig: 'SINGLE_COL',
    });
  },

  createRequestedNeedleModel: (): TLiftedFormModel => {
    return PIPELINE_FORM.sanitize({
      inputs: [
        {
          input: {
            type: 'SEARCH',
            searchOnSearchTextClick: true,
          },
          metadata: {
            keyName: 'needleNumber',
            removeFromPayload: true,
            label: 'tm_raise_request.NeedleNumber',
            isRequired: true,
          },
        },
        {
          input: { type: 'DROPDOWN_WITH_SEARCH' },
          metadata: {
            keyName: 'needleId',
            label: 'Needle Brand / Needle Type',
            isRequired: true,
            dependsOn: {
              keyName: 'needleNumber',
            },
          },
        },
        {
          input: {
            type: 'INPUT',
            subType: 'number',
            min: 1,
          },
          metadata: {
            keyName: 'quantity',
            label: 'tm_raise_request.NeedleQuantity',
            isRequired: true,
            dependsOn: {
              keyName: 'needleNumber',
            },
          },
        },
      ],
      colConfig: 'SINGLE_COL',
    });
  },

  identity: (): TLiftedFormModel => {
    return {
      inputs: [],
    };
  },

  updateDropdownForKey: (
    key: string,
    liftedFormModel: TLiftedFormModel,
    results: TDropdownItem[],
  ): TLiftedFormModel => {
    return FORM_MODIFIER.updateDropdownForKey(key, liftedFormModel, results);
  },

  onParentSave: (formModel: TLiftedFormModel): TLiftedFormModel => {
    return FORM_MODIFIER.markTouchedInputs(formModel);
  },
};
